import React from 'react';
import PropTypes from 'prop-types';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cn } from '~/lib/utils';

const TabsContext = React.createContext({
	value: undefined,
	variant: 'default',
	onValueChange: undefined
});

const Tabs = React.forwardRef(({
	children,
	value,
	defaultValue,
	onValueChange,
	...props
}, ref) => {
	const [internalValue, setInternalValue] = React.useState(defaultValue);
	const currentValue = value !== undefined ? value : internalValue;

	const handleValueChange = (nextValue) => {
		if(value === undefined) {
			setInternalValue(nextValue);
		}

		if(onValueChange) {
			onValueChange(nextValue);
		}
	};

	return (
		<TabsContext.Provider value={{ value: currentValue, variant: 'default', onValueChange: handleValueChange }}>
			<TabsPrimitive.Root
				ref={ref}
				value={currentValue}
				defaultValue={defaultValue}
				onValueChange={handleValueChange}
				{...props}
			>
				{children}
			</TabsPrimitive.Root>
		</TabsContext.Provider>
	);
});
Tabs.displayName = TabsPrimitive.Root.displayName;
Tabs.propTypes = {
	children: PropTypes.node,
	value: PropTypes.string,
	defaultValue: PropTypes.string,
	onValueChange: PropTypes.func
};

const TabsList = React.forwardRef(({ className, variant = 'default', ...props }, ref) => {
	const context = React.useContext(TabsContext);

	return (
		<TabsContext.Provider value={{ ...context, variant }}>
			<TabsPrimitive.List
				ref={ref}
				className={cn(
					variant === 'line'
						? 'mb-4 flex items-end gap-6 border-b border-border outline-none'
						: 'inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground outline-none',
					className
				)}
				{...props}
			/>
		</TabsContext.Provider>
	);
});
TabsList.displayName = TabsPrimitive.List.displayName;
TabsList.propTypes = {
	className: PropTypes.string,
	variant: PropTypes.oneOf(['default', 'line'])
};

const TabsTrigger = React.forwardRef(({ className, children, value, ...props }, ref) => {
	const context = React.useContext(TabsContext);
	const isActive = context.value === value;
	const isLine = context.variant === 'line';

	return (
		<TabsPrimitive.Trigger
			ref={ref}
			value={value}
			className={cn(
				isLine
					? 'relative border-b-2 border-transparent pb-2 text-sm font-bold outline-none transition-colors duration-100 focus:outline-none focus-visible:outline-none focus-visible:ring-0'
					: 'inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium outline-none ring-offset-background transition-all focus:outline-none focus-visible:outline-none focus-visible:ring-0 disabled:pointer-events-none disabled:opacity-50',
				isLine
					? (isActive ? 'border-primary text-foreground' : 'text-muted-foreground hover:text-foreground')
					: (isActive ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'),
				className
			)}
			{...props}
		>
			{children}
		</TabsPrimitive.Trigger>
	);
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;
TabsTrigger.propTypes = {
	className: PropTypes.string,
	children: PropTypes.node,
	value: PropTypes.string.isRequired
};

const TabsContent = React.forwardRef(({ className, ...props }, ref) => (
	<TabsPrimitive.Content
		ref={ref}
		className={cn('outline-none', className)}
		{...props}
	/>
));
TabsContent.displayName = TabsPrimitive.Content.displayName;
TabsContent.propTypes = { className: PropTypes.string };

export { Tabs, TabsList, TabsTrigger, TabsContent };
